import { supabase } from '@/config/supabase';
import { batchGetImageUrls, getPersonImageUrl } from './storage.api';
import type {
  TreeCanvasData,
  PersonDetailData,
  RelatedPerson,
  TreeDataError,
  CreateTreeRpcResponse,
} from '@/types/tree-dashboard.types';

/**
 * Fetch tree canvas data for dashboard
 * Returns lightweight data for initial render
 *
 * @param treeId - Optional specific tree ID. If omitted, fetches user's primary tree
 * @returns Promise<TreeCanvasData>
 * @throws TreeDataError
 */
export async function fetchTreeCanvasData(treeId?: string): Promise<TreeCanvasData> {
  try {
    console.log('[Tree API] Fetching tree canvas data...', { treeId });

    // Step 1: Get tree + membership
    let treeQuery = supabase
      .from('trees')
      .select(`
        id,
        name,
        description,
        owner_id,
        visibility,
        current_person_count,
        person_limit,
        is_premium,
        created_at,
        updated_at,
        tree_memberships!inner(role, accepted_at)
      `)
      .not('tree_memberships.accepted_at', 'is', null)
      .is('deleted_at', null);

    if (treeId) {
      treeQuery = treeQuery.eq('id', treeId);
    } else {
      treeQuery = treeQuery.order('updated_at', { ascending: false }).limit(1);
    }

    // Use maybeSingle() instead of single() to safely handle 0 rows (no trees)
    const { data: treeData, error: treeError } = await treeQuery.maybeSingle();

    if (treeError) {
      console.error('[Tree API] Tree fetch error:', treeError);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to load tree data',
        details: treeError,
      } as TreeDataError;
    }

    // Handle NO-TREE STATE: User has completed onboarding but has no tree yet
    // This is a valid state (e.g., after welcome-page skip)
    if (!treeData) {
      console.log('[Tree API] No tree found - user has no trees yet (valid NO-TREE state)');
      throw {
        code: 'TREE_NOT_FOUND',
        message: 'No tree found',
        details: { reason: 'User has not created a tree yet' },
      } as TreeDataError;
    }

    console.log('[Tree API] Tree fetched successfully:', treeData.name);

    const resolvedTreeId = treeData.id;
    const membership = (treeData as any).tree_memberships[0];

    // Step 2: Get persons (lightweight) - with Phase 2 fields
    const { data: persons, error: personsError } = await supabase
      .from('persons')
      .select('id, first_name, last_name, gender, date_of_birth, date_of_death, profile_image_url, yob, yod, date_of_birth_qualifier, date_of_death_qualifier, father_id, mother_id, parents_id')
      .eq('tree_id', resolvedTreeId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });

    if (personsError) {
      console.error('[Tree API] Persons fetch error:', personsError);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to load persons',
        details: personsError,
      } as TreeDataError;
    }

    console.log(`[Tree API] Fetched ${persons?.length || 0} persons`);

    // Step 3: Get relationships
    const { data: relationships, error: relationshipsError } = await supabase
      .from('relationships')
      .select('id, person_a_id, person_b_id, relationship_type')
      .eq('tree_id', resolvedTreeId)
      .is('deleted_at', null);

    if (relationshipsError) {
      console.error('[Tree API] Relationships fetch error:', relationshipsError);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to load relationships',
        details: relationshipsError,
      } as TreeDataError;
    }

    console.log(`[Tree API] Fetched ${relationships?.length || 0} relationships`);

    // Step 4: Get couples (Phase 3 Slice 4)
    const { data: couples, error: couplesError } = await supabase
      .from('couples')
      .select('*')
      .eq('tree_id', resolvedTreeId)
      .is('deleted_at', null);

    if (couplesError) {
      console.error('[Tree API] Couples fetch error:', couplesError);
      // Non-fatal: continue without couples data
    }

    console.log(`[Tree API] Fetched ${couples?.length || 0} couples`);

    // Compute age_display client-side
    const personsWithAge = (persons || []).map(p => ({
      ...p,
      age_display: calculateAgeDisplay(p.date_of_birth, p.date_of_death),
    }));

    // Batch fetch image URLs
    const imagePaths = personsWithAge
      .map(p => p.profile_image_url)
      .filter(url => url !== null) as string[];

    const imageUrlMap = await batchGetImageUrls(imagePaths);

    // Attach signed URLs
    const personsWithImages = personsWithAge.map(p => ({
      ...p,
      signedImageUrl: p.profile_image_url ? imageUrlMap.get(p.profile_image_url) || null : null,
    }));

    return {
      tree: {
        id: treeData.id,
        name: treeData.name,
        description: treeData.description,
        owner_id: treeData.owner_id,
        visibility: treeData.visibility,
        current_person_count: treeData.current_person_count,
        person_limit: treeData.person_limit,
        is_premium: treeData.is_premium,
        created_at: treeData.created_at,
        updated_at: treeData.updated_at,
      },
      membership: {
        role: membership.role,
        accepted_at: membership.accepted_at,
      },
      persons: personsWithImages,
      relationships: relationships || [],
      couples: couples || [],  // Phase 3 Slice 4: Include couples data
    };
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Unexpected error loading tree data',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Fetch full person details for right panel
 * Loaded separately when person is selected
 *
 * @param personId - ID of the person to load
 * @returns Promise<PersonDetailData>
 * @throws TreeDataError
 */
export async function fetchPersonDetail(personId: string): Promise<PersonDetailData> {
  try {
    console.log('[Tree API] Fetching person details:', personId);

    // Main person data
    const { data: person, error: personError } = await supabase
      .from('persons')
      .select('*')
      .eq('id', personId)
      .is('deleted_at', null)
      .single();

    if (personError || !person) {
      console.error('[Tree API] Person fetch error:', personError);
      throw {
        code: 'PERSON_NOT_FOUND',
        message: 'Person not found',
        details: personError,
      } as TreeDataError;
    }

    // Fetch related persons in parallel
    const [parentsResult, childrenResult, spousesResult, siblingsResult] = await Promise.all([
      fetchParents(personId),
      fetchChildren(personId),
      fetchSpouses(personId),
      fetchSiblings(personId),
    ]);

    // Get signed URL for main person image
    let signedImageUrl: string | null = null;
    if (person.profile_image_url) {
      signedImageUrl = await getPersonImageUrl(person.profile_image_url);
    }

    console.log('[Tree API] Person details loaded successfully');

    return {
      person: {
        ...person,
        age: calculateAge(person.date_of_birth, person.date_of_death),
        lifespan_years: calculateLifespan(person.date_of_birth, person.date_of_death),
        signedImageUrl,
      },
      parents: parentsResult,
      children: childrenResult,
      spouses: spousesResult,
      siblings: siblingsResult,
    };
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to load person details',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Helper: Fetch parents
 */
async function fetchParents(personId: string): Promise<RelatedPerson[]> {
  // Get parent IDs from relationships
  const { data: rels, error: relError } = await supabase
    .from('relationships')
    .select('person_a_id')
    .eq('person_b_id', personId)
    .eq('relationship_type', 'parent_child')
    .is('deleted_at', null);

  if (relError) {
    console.error('[Tree API] Failed to fetch parent relationships:', relError);
    return [];
  }

  const parentIds = (rels || []).map(r => r.person_a_id);
  if (parentIds.length === 0) return [];

  // Fetch parent details
  const { data, error } = await supabase
    .from('persons')
    .select('id, first_name, last_name, gender, profile_image_url, date_of_birth, date_of_death')
    .in('id', parentIds)
    .is('deleted_at', null);

  if (error) {
    console.error('[Tree API] Failed to fetch parent persons:', error);
    return [];
  }

  return (data || []).map(p => ({ ...p, relationship_type: 'parent' as const }));
}

/**
 * Helper: Fetch children
 */
async function fetchChildren(personId: string): Promise<RelatedPerson[]> {
  // Get child IDs from relationships
  const { data: rels, error: relError } = await supabase
    .from('relationships')
    .select('person_b_id')
    .eq('person_a_id', personId)
    .eq('relationship_type', 'parent_child')
    .is('deleted_at', null);

  if (relError) {
    console.error('[Tree API] Failed to fetch child relationships:', relError);
    return [];
  }

  const childIds = (rels || []).map(r => r.person_b_id);
  if (childIds.length === 0) return [];

  // Fetch child details
  const { data, error } = await supabase
    .from('persons')
    .select('id, first_name, last_name, gender, profile_image_url, date_of_birth, date_of_death')
    .in('id', childIds)
    .is('deleted_at', null);

  if (error) {
    console.error('[Tree API] Failed to fetch child persons:', error);
    return [];
  }

  return (data || []).map(p => ({ ...p, relationship_type: 'child' as const }));
}

/**
 * Helper: Fetch spouses
 */
async function fetchSpouses(personId: string): Promise<RelatedPerson[]> {
  // Get relationships where person is either person_a or person_b
  const { data: rels, error: relError } = await supabase
    .from('relationships')
    .select('person_a_id, person_b_id')
    .eq('relationship_type', 'spouse')
    .or(`person_a_id.eq.${personId},person_b_id.eq.${personId}`)
    .is('deleted_at', null);

  if (relError) {
    console.error('[Tree API] Failed to fetch spouse relationships:', relError);
    return [];
  }

  // Extract spouse IDs
  const spouseIds = (rels || []).map(r =>
    r.person_a_id === personId ? r.person_b_id : r.person_a_id
  );

  if (spouseIds.length === 0) return [];

  // Fetch spouse details
  const { data, error } = await supabase
    .from('persons')
    .select('id, first_name, last_name, gender, profile_image_url, date_of_birth, date_of_death')
    .in('id', spouseIds)
    .is('deleted_at', null);

  if (error) {
    console.error('[Tree API] Failed to fetch spouse persons:', error);
    return [];
  }

  return (data || []).map(p => ({ ...p, relationship_type: 'spouse' as const }));
}

/**
 * Helper: Fetch siblings (shared parents)
 */
async function fetchSiblings(personId: string): Promise<RelatedPerson[]> {
  // Get parent IDs
  const { data: parentRels, error: parentError } = await supabase
    .from('relationships')
    .select('person_a_id')
    .eq('person_b_id', personId)
    .eq('relationship_type', 'parent_child')
    .is('deleted_at', null);

  if (parentError) {
    console.error('[Tree API] Failed to fetch parent relationships for siblings:', parentError);
    return [];
  }

  const parentIds = (parentRels || []).map(r => r.person_a_id);

  if (parentIds.length === 0) return [];

  // Get all children of those parents (excluding self)
  const { data: siblingRels, error: siblingError } = await supabase
    .from('relationships')
    .select('person_b_id')
    .in('person_a_id', parentIds)
    .eq('relationship_type', 'parent_child')
    .neq('person_b_id', personId)
    .is('deleted_at', null);

  if (siblingError) {
    console.error('[Tree API] Failed to fetch sibling relationships:', siblingError);
    return [];
  }

  const siblingIds = [...new Set((siblingRels || []).map(r => r.person_b_id))];

  if (siblingIds.length === 0) return [];

  // Fetch sibling details
  const { data, error } = await supabase
    .from('persons')
    .select('id, first_name, last_name, gender, profile_image_url, date_of_birth, date_of_death')
    .in('id', siblingIds)
    .is('deleted_at', null);

  if (error) {
    console.error('[Tree API] Failed to fetch sibling persons:', error);
    return [];
  }

  return (data || []).map(p => ({ ...p, relationship_type: 'sibling' as const }));
}

/**
 * Helper: Calculate age display string
 */
function calculateAgeDisplay(birthDate: string | null, deathDate: string | null): string | null {
  if (!birthDate) return null;

  const birth = new Date(birthDate);
  const end = deathDate ? new Date(deathDate) : new Date();

  const years = Math.floor((end.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

  return years >= 0 ? years.toString() : null;
}

/**
 * Helper: Calculate current age or age at death
 */
function calculateAge(birthDate: string | null, deathDate: string | null): number | null {
  if (!birthDate) return null;

  const birth = new Date(birthDate);
  const end = deathDate ? new Date(deathDate) : new Date();

  const years = Math.floor((end.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

  return years >= 0 ? years : null;
}

/**
 * Helper: Calculate lifespan (only if deceased)
 */
function calculateLifespan(birthDate: string | null, deathDate: string | null): number | null {
  if (!birthDate || !deathDate) return null;

  const birth = new Date(birthDate);
  const death = new Date(deathDate);

  const years = Math.floor((death.getTime() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

  return years >= 0 ? years : null;
}

/**
 * Update person details
 *
 * @param personId - ID of person to update
 * @param updates - Partial person data to update
 * @returns Promise<PersonDetailFull>
 * @throws TreeDataError
 */
export async function updatePerson(
  personId: string,
  updates: {
    first_name?: string;
    last_name?: string | null;
    nickname?: string | null;
    gender?: 'male' | 'female' | 'other';
    date_of_birth?: string | null;
    date_of_death?: string | null;
    marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | null;
    notes?: string | null;
    profile_image_url?: string | null;
  }
): Promise<any> {
  try {
    console.log('[Tree API] Updating person:', personId, updates);

    const { data, error } = await supabase
      .from('persons')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', personId)
      .select('*')
      .single();

    if (error || !data) {
      console.error('[Tree API] Person update error:', error);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to update person',
        details: error,
      } as TreeDataError;
    }

    console.log('[Tree API] Person updated successfully');

    // Get signed URL for image if present
    let signedImageUrl: string | null = null;
    if (data.profile_image_url) {
      signedImageUrl = await getPersonImageUrl(data.profile_image_url);
    }

    return {
      ...data,
      age: calculateAge(data.date_of_birth, data.date_of_death),
      lifespan_years: calculateLifespan(data.date_of_birth, data.date_of_death),
      signedImageUrl,
    };
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to update person',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Create a new person in a tree
 *
 * @param treeId - ID of the tree
 * @param personData - Person data to create
 * @returns Promise<string> - ID of created person
 * @throws TreeDataError
 */
export async function createPerson(
  treeId: string,
  personData: {
    first_name: string;
    last_name?: string | null;
    nickname?: string | null;
    gender: 'male' | 'female' | 'other';
    date_of_birth?: string | null;
    date_of_death?: string | null;
    marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | null;
    birth_place?: string | null;
    death_place?: string | null;
    notes?: string | null;
    profile_image_url?: string | null;
  }
): Promise<string> {
  try {
    console.log('[Tree API] Creating person in tree:', treeId, personData);

    const { data, error } = await supabase
      .from('persons')
      .insert({
        tree_id: treeId,
        ...personData,
      })
      .select('id')
      .single();

    if (error || !data) {
      console.error('[Tree API] Person create error:', error);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to create person',
        details: error,
      } as TreeDataError;
    }

    console.log('[Tree API] Person created successfully:', data.id);
    return data.id;
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to create person',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Create a relationship between two persons
 *
 * @param treeId - ID of the tree
 * @param personAId - ID of person A (parent for parent_child, either for spouse)
 * @param personBId - ID of person B (child for parent_child, either for spouse)
 * @param relationshipType - Type of relationship
 * @returns Promise<string> - ID of created relationship
 * @throws TreeDataError
 */
export async function createRelationship(
  treeId: string,
  personAId: string,
  personBId: string,
  relationshipType: 'parent_child' | 'spouse'
): Promise<string> {
  try {
    console.log('[Tree API] Creating relationship:', { treeId, personAId, personBId, relationshipType });

    const { data, error } = await supabase
      .from('relationships')
      .insert({
        tree_id: treeId,
        person_a_id: personAId,
        person_b_id: personBId,
        relationship_type: relationshipType,
      })
      .select('id')
      .single();

    if (error || !data) {
      console.error('[Tree API] Relationship create error:', error);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to create relationship',
        details: error,
      } as TreeDataError;
    }

    console.log('[Tree API] Relationship created successfully:', data.id);
    return data.id;
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to create relationship',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Create a couple record (for spouse relationships with metadata)
 *
 * @param treeId - ID of the tree
 * @param person1Id - ID of first person in couple
 * @param person2Id - ID of second person in couple
 * @param coupleMetadata - Optional metadata (start/end years, marriage status, etc.)
 * @returns Promise<string> - ID of created couple record
 * @throws TreeDataError
 */
export async function createCouple(
  treeId: string,
  person1Id: string,
  person2Id: string,
  coupleMetadata?: {
    date_start?: number;   // Year only (YYYY)
    date_end?: number;     // Year only (YYYY)
    is_married?: boolean;
    has_ended?: boolean;
  }
): Promise<string> {
  try {
    console.log('[Tree API] Creating couple record:', { treeId, person1Id, person2Id, coupleMetadata });

    // Convert year numbers to date strings (YYYY-01-01 format for storage)
    // The couples table uses string dates, but we accept year-only input from UI
    const dateStart = coupleMetadata?.date_start
      ? `${coupleMetadata.date_start}-01-01`
      : null;
    const dateEnd = coupleMetadata?.date_end
      ? `${coupleMetadata.date_end}-01-01`
      : null;

    // Build insert object conditionally based on provided metadata
    // This allows truly optional metadata (database defaults will be used if not provided)
    const insertData: Record<string, any> = {
      tree_id: treeId,
      person1_id: person1Id,
      person2_id: person2Id,
      date_start: dateStart,
      date_end: dateEnd,
    };

    // Only include is_married and has_ended if explicitly provided
    // This avoids forcing false as a semantic claim when metadata is intentionally omitted
    if (coupleMetadata?.is_married !== undefined) {
      insertData.is_married = coupleMetadata.is_married;
    }
    if (coupleMetadata?.has_ended !== undefined) {
      insertData.has_ended = coupleMetadata.has_ended;
    }

    const { data, error } = await supabase
      .from('couples')
      .insert(insertData)
      .select('id')
      .single();

    if (error || !data) {
      console.error('[Tree API] Couple create error:', error);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to create couple record',
        details: error,
      } as TreeDataError;
    }

    console.log('[Tree API] Couple record created successfully:', data.id);
    return data.id;
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to create couple record',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Delete person (soft delete by setting deleted_at)
 *
 * @param personId - ID of person to delete
 * @returns Promise<void>
 * @throws TreeDataError
 */
export async function deletePerson(personId: string): Promise<void> {
  try {
    console.log('[Tree API] Deleting person:', personId);

    // Soft delete by setting deleted_at timestamp
    const { error } = await supabase
      .from('persons')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', personId);

    if (error) {
      console.error('[Tree API] Person delete error:', error);
      throw {
        code: 'NETWORK_ERROR',
        message: 'Failed to delete person',
        details: error,
      } as TreeDataError;
    }

    // Also soft delete all relationships involving this person
    await supabase
      .from('relationships')
      .update({ deleted_at: new Date().toISOString() })
      .or(`person_a_id.eq.${personId},person_b_id.eq.${personId}`);

    console.log('[Tree API] Person deleted successfully');
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to delete person',
      details: error,
    } as TreeDataError;
  }
}

/**
 * Create tree with self person (from user profile)
 * Uses RPC function to atomically create tree + self person + membership
 *
 * @returns Promise<{ treeId: string, personId: string }>
 * @throws TreeDataError
 */
export async function createTreeWithSelfPerson(): Promise<{ treeId: string; personId: string }> {
  try {
    console.log('[Tree API] Creating tree with self person...');

    const { data, error } = await supabase.rpc('create_tree_with_self_person');

    if (error) {
      console.error('[Tree API] RPC error:', error);
      throw {
        code: 'RPC_ERROR',
        message: error.message,
      } as TreeDataError;
    }

    // NEW: data is now a direct jsonb object, not an array
    // Type assertion: Supabase RPC returns unknown, we know the shape from backend
    const result = data as CreateTreeRpcResponse | null;

    if (!result) {
      throw {
        code: 'NO_RESULT',
        message: 'RPC returned no result',
      } as TreeDataError;
    }

    // Check backend-reported success
    if (!result.success) {
      // Log full diagnostic info for debugging
      console.error('[Tree API] Backend reported failure:', {
        message: result.error_message,
        code: result.error_code,
        detail: result.error_detail,
        hint: result.error_hint,
        context: result.error_context,
      });

      throw {
        code: result.error_code || 'BACKEND_ERROR',
        message: result.error_message || 'Failed to create tree',
        details: {
          error_detail: result.error_detail,
          error_hint: result.error_hint,
        },
      } as TreeDataError;
    }

    console.log('[Tree API] Tree created successfully:', {
      treeId: result.tree_id,
      personId: result.person_id,
    });

    return {
      treeId: result.tree_id!,
      personId: result.person_id!,
    };
  } catch (error) {
    if ((error as TreeDataError).code) {
      throw error;
    }
    console.error('[Tree API] Unexpected error:', error);
    throw {
      code: 'UNKNOWN',
      message: 'Failed to create tree with self person',
      details: error,
    } as TreeDataError;
  }
}
